import React, { useEffect, useMemo, useState } from "react";
import { useHistory } from "react-router-dom";
import { IoAlbums, IoFlash, IoRadioButtonOn } from "react-icons/io5";
import MdiIcon from "../../components/device/app/MdiIcon";
import { mdiHomeEdit } from "@mdi/js";
import Main from "../../components/layout/Main";
import AppModal from "../../components/layout/app/AppModal";
import AlertDialog from "../../components/layout/app/AlertDialog";
import CreatePlaceDialog from "../../components/places/CreatePlaceDialog";
import { useToggleTop } from "../../components/common/ToggleTop";
import { apiMessage } from "../../api/apiMessage";
import { UserService } from "../../services/UserService";
import usePropertyDevices from "../../hooks/usePropertyDevices";
import { usePropertyRole } from "../../hooks/usePropertyOwner";

// Floor tabs as in the app (app/components/TabBar/TopTabBar.js).
export const Chips = ({ items, active, onSelect, label }) => (
  <div className="hx-chips" role="tablist" aria-label={label}>
    {items.map((item) => (
      <button
        key={item.key}
        type="button"
        role="tab"
        aria-selected={item.key === active}
        className={`hx-chip ${item.key === active ? "is-active" : ""}`}
        onClick={() => onSelect(item.key)}
      >
        {item.label}
      </button>
    ))}
  </div>
);

const plural = (count, word) => `${count} ${word}${count === 1 ? "" : "s"}`;

// Room card (app/components/RoomCardComponent.js). The app shows placeholder
// temperature and power figures; here the card shows the room's real device
// counts instead.
const RoomCard = ({ room, index, deviceCount, runningCount, onOpen }) => (
  <button
    type="button"
    className="hx-room"
    style={{ "--i": index, backgroundImage: room.imageUri ? `url("${room.imageUri}")` : undefined }}
    onClick={onOpen}
  >
    <span className="hx-room-inner">
      <span className="hx-room-name">{room.name}</span>
      <span className="hx-room-foot">
        <span className="hx-room-stat">
          <IoFlash style={{ color: "rgba(255, 204, 59, 1)" }} aria-hidden="true" />
          {plural(deviceCount, "device")}
        </span>
        <span className="hx-room-stat">
          <IoRadioButtonOn style={{ color: "#47e888" }} aria-hidden="true" />
          {runningCount ? `${runningCount} running` : "None running"}
        </span>
      </span>
    </span>
  </button>
);

// Floors View tab: one tab per floor and a card per room.
function Floors() {
  const navigate = useHistory();
  const toast = useToggleTop();
  const { devices, floors, error, propertyId, reload } = usePropertyDevices();
  const role = usePropertyRole();
  const [activeFloor, setActiveFloor] = useState(null);
  const [editing, setEditing] = useState(false);
  const [floorName, setFloorName] = useState("");
  const [saving, setSaving] = useState(false);
  // "floor" or "room" while its create dialog is open
  const [creating, setCreating] = useState(null);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const isOwner = role === "owner";

  useEffect(() => {
    if (floors?.length && !floors.some((floor) => floor._id === activeFloor)) {
      setActiveFloor(floors[0]._id);
    }
  }, [floors, activeFloor]);

  const floor = floors?.find((item) => item._id === activeFloor);

  const counts = useMemo(() => {
    const byRoom = {};
    (devices ?? []).forEach((device) => {
      const key = `${device.floorId}/${device.roomId}`;
      byRoom[key] = byRoom[key] ?? { total: 0, running: 0 };
      byRoom[key].total += 1;
      if (device.props?.state === true) byRoom[key].running += 1;
    });
    return byRoom;
  }, [devices]);

  const openRoom = (room) =>
    navigate.push("/Room", { params: { floorName: floor?.name, roomName: room.name, roomId: room._id } });

  const startEditing = () => {
    // The API only lets owners change the property, as the app tells guests.
    if (role === "guest") {
      toast("Only an owner can edit floors.", "red");
      return;
    }
    setFloorName(floor?.name ?? "");
    setEditing(true);
  };

  // Renames the floor in the property config, as the old Property page did.
  const saveFloorName = async (event) => {
    event.preventDefault();
    const name = floorName.trim();
    if (!name || name === floor?.name) {
      setEditing(false);
      return;
    }
    setSaving(true);
    try {
      const config = await UserService.getConfig(propertyId);
      config.floors = config.floors.map((item) => (item._id === floor._id ? { ...item, name } : item));
      const response = await UserService.updateConfig(config, propertyId);
      if (response !== "Updated successfully") throw new Error(String(response));
      toast("Floor name updated", "green");
      setEditing(false);
      reload();
    } catch (err) {
      toast(apiMessage(err, "Couldn't update the floor name"), "red");
    } finally {
      setSaving(false);
    }
  };

  // Only an empty floor can go, as in the app: the API removes a floor by name
  // with whatever is on it.
  const deleteFloor = async () => {
    setConfirmDelete(false);
    if (!floor || floor.rooms?.length) return;
    try {
      await UserService.deleteFloor(propertyId, { name: floor.name });
      toast(`Floor "${floor.name}" deleted`, "green");
      reload();
    } catch (err) {
      toast(apiMessage(err, "Couldn't delete the floor. Please try again."), "red");
    }
  };

  return (
    <Main header="tab" title="Floors View">
      {floors === null ? (
        <div className="hx-rooms" style={{ marginTop: 70 }}>
          <div className="hx-skel" style={{ height: 220 }} />
          <div className="hx-skel" style={{ height: 220 }} />
        </div>
      ) : error ? (
        <div className="hx-empty" role="alert">
          Couldn't load the floors. Check your connection and reload the page.
        </div>
      ) : !floors.length ? (
        <div className="hx-empty-page">
          <IoAlbums aria-hidden="true" />
          <p>No floors yet</p>
          {isOwner ? (
            <button type="button" className="hx-btn" onClick={() => setCreating("floor")}>
              Add floor
            </button>
          ) : (
            <span>The home's owner adds its floors and rooms.</span>
          )}
        </div>
      ) : (
        <>
          <Chips
            label="Floors"
            items={floors.map((item) => ({ key: item._id, label: item.name }))}
            active={activeFloor}
            onSelect={setActiveFloor}
          />
          {floor?.rooms?.length ? (
            <div className="hx-rooms" key={activeFloor}>
              {floor.rooms.map((room, index) => {
                const count = counts[`${floor._id}/${room._id}`] ?? { total: 0, running: 0 };
                return (
                  <RoomCard
                    key={room._id}
                    room={room}
                    index={index}
                    deviceCount={count.total}
                    runningCount={count.running}
                    onOpen={() => openRoom(room)}
                  />
                );
              })}
            </div>
          ) : (
            <div className="hx-empty-page">
              <IoAlbums aria-hidden="true" />
              <p>No rooms on this floor</p>
              {isOwner ? (
                <div className="hx-empty-actions">
                  <button type="button" className="hx-btn" onClick={() => setCreating("room")}>
                    Add room
                  </button>
                  <button type="button" className="hx-btn is-ghost is-danger-text" onClick={() => setConfirmDelete(true)}>
                    Delete floor
                  </button>
                </div>
              ) : (
                <span>The home's owner adds its rooms.</span>
              )}
            </div>
          )}
          <button type="button" className="hx-fab" onClick={startEditing} aria-label={`Rename ${floor?.name ?? "floor"}`}>
            <MdiIcon path={mdiHomeEdit} size={28} />
          </button>
        </>
      )}

      <AppModal
        open={editing}
        onClose={() => setEditing(false)}
        title="Edit Floor"
        actions={
          <>
            <button type="button" className="hx-btn is-ghost" onClick={() => setEditing(false)}>
              Cancel
            </button>
            <button type="submit" form="hx-floor-form" className="hx-btn" disabled={saving}>
              {saving ? "Saving…" : "Update"}
            </button>
          </>
        }
      >
        <form id="hx-floor-form" onSubmit={saveFloorName}>
          <label className="hx-label" htmlFor="hx-floor-name">
            Floor name
          </label>
          <input
            id="hx-floor-name"
            className="hx-input"
            value={floorName}
            onChange={(event) => setFloorName(event.target.value)}
            autoComplete="off"
          />
        </form>
      </AppModal>
      <CreatePlaceDialog
        kind={creating ?? "floor"}
        open={Boolean(creating)}
        floorName={floor?.name}
        onClose={() => setCreating(null)}
      />
      <AlertDialog
        open={confirmDelete}
        type="confirmation"
        title="Delete Floor"
        message={`Are you sure you want to delete the floor "${floor?.name ?? ""}"?`}
        confirmLabel="Delete"
        onClose={() => setConfirmDelete(false)}
        onConfirm={deleteFloor}
      />
    </Main>
  );
}

export default Floors;
